/** Shared signup / auth helpers for registration steps 2+. */
export const SIGNUP_USER_KEY = 'DarkCarz-signup-user';
export const MERCHANT_DATA_KEY = 'DarkCarz-merchant-data';
export const AUTH_TOKEN_KEY = 'token';
export const PAYOUT_SUBMITTED_KEY = 'reg-payout-submitted';

/**
 * Business signup step numbers (signupStepCompleted):
 * 0 = registration done → Verification
 * 1 = ID docs done → Payout setup
 * 2 = payout done → TCP & insurance
 * 3 = insurance done / signup complete
 */
export const SIGNUP_ROUTES = {
  details: '/registration/registration-details',
  verification: '/registration/verification',
  payout: '/registration/payout-setup',
  payoutReturn: '/registration/payout-return',
  /** @deprecated use payout */
  bank: '/registration/payout-setup',
  insurance: '/registration/insurance-details',
  vehicles: '/registration/vehicles',
  drivers: '/registration/drivers/profile-details'
};

function normalizeUser(data: any): any {
  if (!data) {
    return null;
  }
  return data.account || data.merchant || data.user || data;
}

export function isPayoutStepSubmitted(): boolean {
  try {
    return localStorage.getItem(PAYOUT_SUBMITTED_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export function clearPayoutStepSubmitted(): void {
  try {
    localStorage.removeItem(PAYOUT_SUBMITTED_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * Persist merchant profile + session token after login/register.
 * Prefer API `token` (sessionId) for Authorization: Bearer.
 */
export function persistMerchantAuth(data: any, preferredId?: string): any {
  const user = normalizeUser(data);
  if (!user) {
    return null;
  }
  if (preferredId && !user._id && !user.id) {
    user._id = preferredId;
  }
  // Preserve step fields from top-level API payload when nested under account
  if (data && data !== user) {
    if (typeof data.signupStepCompleted === 'number' && user.signupStepCompleted == null) {
      user.signupStepCompleted = data.signupStepCompleted;
    }
    if (typeof data.isSignupCompleted === 'boolean' && user.isSignupCompleted == null) {
      user.isSignupCompleted = data.isSignupCompleted;
    }
    if (typeof data.hasVehicles === 'boolean') {
      user.hasVehicles = data.hasVehicles;
    }
    if (typeof data.hasDrivers === 'boolean') {
      user.hasDrivers = data.hasDrivers;
    }
  }
  localStorage.setItem(SIGNUP_USER_KEY, JSON.stringify(user));
  localStorage.setItem(MERCHANT_DATA_KEY, JSON.stringify(user));

  const token =
    (data && (data.token || data.sessionId)) ||
    user.token ||
    user.sessionId ||
    '';
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, String(token));
  }
  return user;
}

export function saveSignupUser(data: any): void {
  persistMerchantAuth(data);
}

export function getSignupUser(): any {
  try {
    const raw = localStorage.getItem(SIGNUP_USER_KEY) || localStorage.getItem(MERCHANT_DATA_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function getSignupUserId(): string {
  const user = getSignupUser();
  if (!user) {
    return '';
  }
  return user._id || user.id || user.merchantId || user.userId || '';
}

export function getAuthToken(): string {
  return localStorage.getItem(AUTH_TOKEN_KEY) || '';
}

export function clearSignupUser(): void {
  localStorage.removeItem(SIGNUP_USER_KEY);
}

export function clearMerchantAuth(): void {
  localStorage.removeItem(SIGNUP_USER_KEY);
  localStorage.removeItem(MERCHANT_DATA_KEY);
  localStorage.removeItem(AUTH_TOKEN_KEY);
  clearPayoutStepSubmitted();
}

export function isSignupCompleted(): boolean {
  const user = getSignupUser();
  return !!(user && user.isSignupCompleted);
}

export function getSignupStepCompleted(): number {
  const user = getSignupUser();
  if (!user) {
    return -1;
  }
  if (user.isSignupCompleted) {
    return 3;
  }
  const step = Number(user.signupStepCompleted);
  return isNaN(step) ? 0 : step;
}

/** Update locally after a signup step succeeds (API may not echo user back). */
export function patchSignupProgress(partial: { signupStepCompleted?: number; isSignupCompleted?: boolean }): void {
  const user = getSignupUser() || {};
  if (typeof partial.signupStepCompleted === 'number') {
    user.signupStepCompleted = partial.signupStepCompleted;
  }
  if (typeof partial.isSignupCompleted === 'boolean') {
    user.isSignupCompleted = partial.isSignupCompleted;
  }
  localStorage.setItem(SIGNUP_USER_KEY, JSON.stringify(user));
  localStorage.setItem(MERCHANT_DATA_KEY, JSON.stringify(user));
  if (partial.isSignupCompleted) {
    localStorage.setItem('reg-business-complete', '1');
  }
}

/**
 * Where the user should be in the signup wizard (same rules as login redirect).
 */
export function getAllowedSignupRoute(): string {
  const user = getSignupUser();
  if (!user || !getSignupUserId()) {
    return SIGNUP_ROUTES.details;
  }
  if (user.isSignupCompleted) {
    return SIGNUP_ROUTES.vehicles;
  }
  const step = getSignupStepCompleted();
  if (step <= 0) {
    return SIGNUP_ROUTES.verification;
  }
  // Verification done — need Stripe payout (also covers legacy step-1 verification-only)
  if (step === 1 && !isPayoutStepSubmitted()) {
    return SIGNUP_ROUTES.payout;
  }
  // Payout done (step 2+), or legacy step 1 with payout flag from pre-verification-restore flow
  if (step >= 2 || (step === 1 && isPayoutStepSubmitted())) {
    return SIGNUP_ROUTES.insurance;
  }
  return SIGNUP_ROUTES.payout;
}

/** Post-login destination: unfinished signup, missing fleet, or dashboard. */
export function getPostLoginRoute(): string {
  const user = getSignupUser();
  if (!user || user.isSignupCompleted !== true) {
    return getAllowedSignupRoute();
  }
  if (user.hasVehicles === false) {
    return SIGNUP_ROUTES.vehicles;
  }
  if (user.hasDrivers === false) {
    return SIGNUP_ROUTES.drivers;
  }
  return '/merchant/dashboard';
}

export function getSignupRouteKey(url: string): string {
  const path = (url || '').split('?')[0];
  if (path.indexOf('/registration/drivers') !== -1) {
    return 'drivers';
  }
  if (path.indexOf('/registration/vehicles') !== -1) {
    return 'vehicles';
  }
  if (path.indexOf('/registration/payout-return') !== -1) {
    return 'payout-return';
  }
  if (path.indexOf('/registration/payout-setup') !== -1) {
    return 'payout';
  }
  if (path.indexOf('/registration/verification') !== -1) {
    return 'verification';
  }
  if (path.indexOf('/registration/bank-info') !== -1) {
    return 'bank';
  }
  if (path.indexOf('/registration/insurance-details') !== -1) {
    return 'insurance';
  }
  if (path.indexOf('/registration/registration-details') !== -1) {
    return 'details';
  }
  return 'other';
}

/**
 * Whether the user may be on this registration URL.
 * Completed business steps are locked; vehicles/drivers only after business signup is done.
 */
export function isSignupRouteAllowed(url: string): boolean {
  const key = getSignupRouteKey(url);
  const userId = getSignupUserId();
  const completed = isSignupCompleted();
  const step = getSignupStepCompleted();

  if (key === 'drivers' || key === 'vehicles') {
    return completed;
  }

  if (key === 'details') {
    // Only brand-new signups may fill registration details
    return !userId;
  }

  if (!userId) {
    return false;
  }

  if (completed) {
    // Business wizard finished — block going back into those steps
    return false;
  }

  if (key === 'payout-return') {
    return step >= 1;
  }

  if (key === 'verification') {
    return step <= 0;
  }

  if (key === 'payout' || key === 'bank') {
    if (step >= 2) {
      return false;
    }
    // Need ID verification first (step 1); allow stay on payout until marked complete
    return step === 1;
  }

  if (key === 'insurance') {
    return step >= 2 || (step === 1 && isPayoutStepSubmitted());
  }

  return true;
}

/** Whether a business stepper step id is navigable / past / current. */
export function getBusinessStepAccess(stepId: string): 'locked-past' | 'current' | 'locked-future' {
  const allowed = getAllowedSignupRoute();
  const allowedKey = getSignupRouteKey(allowed);
  const order = ['details', 'verification', 'payout', 'insurance'];
  const idToKey: { [id: string]: string } = {
    'registration-details': 'details',
    'verification': 'verification',
    'payout-setup': 'payout',
    'bank-info': 'payout',
    'tcp-insurance': 'insurance'
  };
  const key = idToKey[stepId] || stepId;
  const targetIdx = order.indexOf(key);
  const currentIdx = order.indexOf(allowedKey === 'payout-return' ? 'payout' : allowedKey);
  if (targetIdx < 0 || currentIdx < 0) {
    return 'locked-future';
  }
  if (targetIdx < currentIdx) {
    return 'locked-past';
  }
  if (targetIdx > currentIdx) {
    return 'locked-future';
  }
  return 'current';
}
